/**
 * This class is an abstract class that can be used to implement a database
 * connection. It is used by the DatabaseManager class to manage multiple
 * database connections.
 */
import { initializeApp } from "firebase/app";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { getDatabase, get, ref, set, update, remove, query, orderByChild, equalTo } from "firebase/database";
export class Database {
    constructor(config, databaseName) {
        this.app = null;
        this.db = null;
        this.auth = null;
        this._consent = "positive";
        this._isSerialNoRegisteredInWarranty = null;
        this.config = config;
        this.databaseName = databaseName;
    }
    get consent() {
        if (this.isSerialNoRegisteredInWarranty)
            return "positive"; // if the user has registered their serial number in warranty, they have consented to data collection under our T&Cs
        return this._consent;
    }
    set consent(value) {
        if (this.isSerialNoRegisteredInWarranty) {
            this._consent = "positive";
            return;
        } // if the user has registered their serial number in warranty, they have consented to data collection under our T&Cs
        this._consent = value;
    }
    get isSerialNoRegisteredInWarranty() {
        return this._isSerialNoRegisteredInWarranty;
    }
    set isSerialNoRegisteredInWarranty(value) {
        this._isSerialNoRegisteredInWarranty = value;
        if (value)
            this._consent = "positive";
    }
    async signIn() {
        try {
            if (!this.app) {
                throw new Error("Database not initialized");
            }
            this.auth = getAuth(this.app);
            await signInWithEmailAndPassword(this.auth, this.config.email, this.config.password);
        }
        catch (error) {
            console.log('Error signing in:', error);
        }
    }
    async initialize() {
        try {
            this.app = initializeApp(this.config, this.databaseName);
            this.db = getDatabase(this.app);
            if (this.config.password) {
                await this.signIn();
            }
        }
        catch (error) {
            console.log(error);
        }
        return this;
    }
    async get(path) {
        try {
            if (!this.db) {
                throw new Error("Database not initialized");
            }
            const dbRef = ref(this.db, path);
            const snapshot = await get(dbRef);
            return snapshot.val();
        }
        catch (error) {
            console.log(error);
            return null;
        }
    }
    async exists(path) {
        try {
            if (!this.db) {
                return false;
            }
            const dbRef = ref(this.db, path);
            const snapshot = await get(dbRef);
            return snapshot.exists();
        }
        catch (error) {
            console.log(error);
            return false;
        }
    }
    async set(value, path) {
        const parsedValue = JSON.parse(JSON.stringify(value));
        try {
            if (!this.db) {
                return false;
            }
            if (this.consent === "negative") {
                return false;
            }
            const dbRef = ref(this.db, path);
            await set(dbRef, parsedValue);
            return true;
        }
        catch (error) {
            console.log(error);
            return false;
        }
    }
    async update(value, path) {
        try {
            if (!this.db) {
                console.warn("Database not initialized");
                return false;
            }
            if (this.consent === "negative") {
                return false;
            }
            const dbRef = ref(this.db, path);
            await update(dbRef, value);
            return true;
        }
        catch (error) {
            console.log(error);
            return false;
        }
    }
    async remove(path) {
        try {
            if (!this.db) {
                return false;
            }
            const dbRef = ref(this.db, path);
            await remove(dbRef);
            return true;
        }
        catch (error) {
            console.log(error);
            return false;
        }
    }
    async filterByProperty(property, value, path = "/") {
        try {
            if (!this.db) {
                return false;
            }
            const dbRef = query(ref(this.db, path), orderByChild(property), equalTo(value));
            const snapshot = await get(dbRef);
            return snapshot.val();
        }
        catch (error) {
            console.log(error);
            return null;
        }
    }
}
